import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../core-components/theme/components/Buttons';
import type { ButtonIntent } from '../../../core-components/theme/components/Buttons';
import { InlineSpinner } from '../../../core-components/theme/components/Buttons/InlineSpinner';
import { WizardShell } from '../../../core-components/theme/components/WizardShell';
import { useDocumentTitle } from '../../passcode-auth';
import { isLastStep, ONBOARDING_STEPS, stepAt } from '../domain/steps';
import type { OnboardingStep, StepEnterFrom, StepNotice } from '../domain/steps';
import type { FinishState } from '../hooks/useWizardFinish';

export interface WizardNav {
  readonly index: number;
  readonly moved: boolean;
  readonly enterFrom: StepEnterFrom;
  readonly next: () => void;
  readonly back: () => void;
  readonly goTo: (step: OnboardingStep) => void;
  readonly finish: () => void;
  readonly finishing: FinishState;
  readonly returns: boolean;
  readonly goesOn: boolean;
}

export interface StepAction {
  readonly label: string;
  readonly onPress?: () => void;
  readonly submits?: string;
  readonly busy?: boolean;
  readonly unavailable?: boolean;
  readonly describedBy?: string;
  readonly intent?: ButtonIntent;
}

export interface StepFrameProps {
  readonly nav: WizardNav;
  readonly title: string;
  readonly titleAdornment?: React.ReactNode;
  readonly description?: React.ReactNode;
  readonly primary?: StepAction;
  readonly secondary?: StepAction;
  readonly tertiary?: StepAction;
  readonly notice?: StepNotice | null;
  readonly skippable?: boolean;
  readonly onBack?: () => void;
  readonly children: React.ReactNode;
}

const stepName = (t: TFunction, step: OnboardingStep): string => {
  switch (step) {
    case 'language':
      return t('onboarding.stepName.language', 'Language');
    case 'phone':
      return t('onboarding.stepName.phone', 'Your phone');
    case 'hear':
      return t('onboarding.stepName.hear', 'What you’ll hear');
    case 'alerts':
      return t('onboarding.stepName.alerts', 'Alerts');
    case 'quiet':
      return t('onboarding.stepName.quiet', 'Quiet hours');
    case 'done':
      return t('onboarding.stepName.done', 'Done');
  }
};

const noticeLineSx = { display: 'flex', alignItems: 'center', gap: 2, minInlineSize: 0 } as const;
const noticeTextSx = { color: 'inherit', minInlineSize: 0 } as const;

const NoticeIcon: React.FC<{ kind: StepNotice['kind'] }> = ({ kind }) => {
  switch (kind) {
    case 'failed':
      return <ErrorOutlineIcon fontSize="small" aria-hidden />;
    case 'paused':
      return <CloudOffOutlinedIcon fontSize="small" aria-hidden />;
    case 'saving':
    case 'loading':
      return <InlineSpinner aria-hidden />;
    case 'info':
      return <InfoOutlinedIcon fontSize="small" aria-hidden />;
  }
};

const noticeText = (t: TFunction, notice: StepNotice): string => {
  switch (notice.kind) {
    case 'paused':
      return notice.text ?? t('common.waitingForConnection', 'Waiting for the connection to come back…');
    case 'saving':
      return t('coreComponents.saveBar.saving', 'Saving…');
    default:
      return notice.text;
  }
};

const NoticeLine: React.FC<{ notice: StepNotice; id: string }> = ({ notice, id }) => {
  const { t } = useTranslation();
  return (
    <Box component="span" id={id} sx={noticeLineSx}>
      <NoticeIcon kind={notice.kind} />
      <Typography component="span" variant="label" sx={noticeTextSx}>
        {noticeText(t, notice)}
      </Typography>
    </Box>
  );
};

const dockButton = (slot: ButtonIntent, action: StepAction | undefined, noticeId: string | undefined): React.ReactElement | undefined =>
  action === undefined ? undefined : (
    <Button
      type={action.submits === undefined ? 'button' : 'submit'}
      form={action.submits}
      intent={action.intent ?? slot}
      busy={action.busy}
      aria-disabled={action.unavailable === true ? true : undefined}
      aria-describedby={action.unavailable === true ? action.describedBy ?? noticeId : undefined}
      onClick={action.onPress}
    >
      {action.label}
    </Button>
  );

export const StepFrame: React.FC<StepFrameProps> = ({
  nav,
  title,
  titleAdornment,
  description,
  primary,
  secondary,
  tertiary,
  notice,
  skippable = true,
  onBack,
  children,
}) => {
  const { t } = useTranslation();
  const noticeId = useId();
  useDocumentTitle(title);
  const last = isLastStep(nav.index);
  const hasNotice = notice !== undefined && notice !== null;
  const describedBy = hasNotice ? noticeId : undefined;

  return (
    <WizardShell
      stepCount={ONBOARDING_STEPS.length}
      activeStep={nav.index}
      title={title}
      titleAdornment={titleAdornment}
      description={description}
      counterLabel={t('onboarding.stepCounterNamed', 'Step {{current}} of {{total}} · {{name}}', {
        current: nav.index + 1,
        total: ONBOARDING_STEPS.length,
        name: stepName(t, stepAt(nav.index)),
      })}
      progressLabel={t('onboarding.progress', 'Onboarding progress')}
      onBack={nav.index > 0 ? onBack ?? nav.back : undefined}
      backLabel={t('onboarding.back', 'Back')}
      headerAction={
        last || !skippable ? undefined : (
          <Button intent="tertiary" onClick={nav.next}>
            {t('onboarding.skip', 'Skip')}
          </Button>
        )
      }
      tertiary={dockButton('tertiary', tertiary, describedBy)}
      secondary={dockButton('secondary', secondary, describedBy)}
      primary={dockButton('primary', primary, describedBy)}
      dockMessage={hasNotice ? <NoticeLine notice={notice} id={noticeId} /> : undefined}
      dockTone={notice?.kind === 'failed' ? 'error' : 'neutral'}
      enterFrom={nav.enterFrom}
      autoFocusTitle={nav.moved}
    >
      {children}
    </WizardShell>
  );
};
